import React from 'react';
import { Building, Users, Calendar, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { companyInfo } from '../mock/data';

const CorporateStructure = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Corporate Structure</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              A unified holding company structure that combines specialized expertise 
              across multiple food manufacturing sectors under one corporate umbrella.
            </p>
          </div>
        </div>
      </section>

      {/* Holding Company Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Teng Food Holdings Pte Ltd
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The parent company established to consolidate and strengthen our collective 
              capabilities in Singapore's food manufacturing industry.
            </p>
          </div>

          {/* Holding Company Details */}
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-blue-600 to-red-600 text-white mb-12">
              <CardContent className="p-8 text-center">
                <Building className="h-16 w-16 mx-auto mb-6" />
                <h3 className="text-3xl font-bold mb-4">{companyInfo.holdingCompany.name}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="text-left">
                    <h4 className="font-semibold mb-2">Company Details</h4>
                    <p className="text-blue-100">UEN: {companyInfo.holdingCompany.uen}</p>
                    <p className="text-blue-100">Established: {companyInfo.holdingCompany.established}</p>
                  </div>
                  
                  <div className="text-left">
                    <h4 className="font-semibold mb-2">Head Office</h4>
                    <p className="text-blue-100">{companyInfo.holdingCompany.address}</p>
                  </div>
                </div>

                <div className="text-left">
                  <h4 className="font-semibold mb-2">Core Services</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {companyInfo.holdingCompany.services.map((service, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <ArrowRight className="h-4 w-4 text-blue-200" />
                        <span className="text-blue-100">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 p-4 bg-black bg-opacity-20 rounded-lg">
                  <p className="text-blue-100">{companyInfo.holdingCompany.description}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Corporate Hierarchy */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Organizational Structure
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our three specialized subsidiaries operate under the holding company umbrella, 
              each bringing unique expertise and market focus.
            </p>
          </div>

          {/* Hierarchy Diagram */}
          <div className="relative">
            {/* Holding Company at Top */}
            <div className="flex justify-center mb-12">
              <Card className="bg-blue-600 text-white">
                <CardContent className="p-6 text-center">
                  <Building className="h-8 w-8 mx-auto mb-2" />
                  <h3 className="font-bold">Teng Food Holdings Pte Ltd</h3>
                  <p className="text-blue-100 text-sm">Parent Company</p>
                </CardContent>
              </Card>
            </div>

            {/* Connection Lines */}
            <div className="flex justify-center mb-8">
              <div className="w-px h-12 bg-gray-300"></div>
            </div>
            <div className="flex justify-center mb-8">
              <div className="w-80 h-px bg-gray-300"></div>
            </div>
            <div className="flex justify-center space-x-32 mb-8">
              <div className="w-px h-12 bg-gray-300"></div>
              <div className="w-px h-12 bg-gray-300"></div>
              <div className="w-px h-12 bg-gray-300"></div>
            </div>

            {/* Subsidiaries */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {Object.entries(companyInfo.subsidiaries).map(([key, subsidiary], index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-blue-600" />
                    </div>
                    
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{subsidiary.name}</h4>
                    <div className="text-sm text-blue-600 font-medium mb-3">{subsidiary.type}</div>
                    
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center justify-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span>Est. {subsidiary.established}</span>
                      </div>
                      <p className="text-xs">UEN: {subsidiary.uen}</p>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">{subsidiary.description}</p>
                    
                    <Button variant="outline" size="sm" className="text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white">
                      <Link to="/businesses" className="flex items-center">
                        View Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Management Philosophy */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Unified Management Approach
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  The holding company structure allows us to leverage synergies across our 
                  subsidiaries while maintaining specialized focus in each business segment. 
                  This approach enables efficient resource allocation, shared best practices, 
                  and coordinated strategic planning.
                </p>
                <p>
                  Each subsidiary operates with autonomy in their specialized areas while 
                  benefiting from the collective strength, shared infrastructure, and 
                  combined expertise of the entire group.
                </p>
                <p>
                  Our centralized procurement, quality assurance, and R&D capabilities 
                  create economies of scale that benefit all our operations and ultimately 
                  provide better value to our customers.
                </p>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Corporate Management"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Strategic Benefits of Our Structure
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Operational Synergies",
                description: "Shared resources and expertise across all subsidiaries"
              },
              {
                title: "Market Coverage",
                description: "Comprehensive halal, non-halal, and specialty food markets"
              },
              {
                title: "Risk Diversification",
                description: "Multiple revenue streams across different market segments"
              },
              {
                title: "Scalable Growth",
                description: "Flexible structure supporting expansion and new ventures"
              }
            ].map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">{index + 1}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateStructure;